import { MapPin, Clock } from "lucide-react";
import { DETAILS } from "@/constants/testIds";
import { WEDDING } from "@/data/wedding";
import { useLang } from "@/i18n/LanguageContext";
import { CallaLilyFlourish } from "@/components/wedding/Flourish";
import { Reveal } from "@/components/motion/Reveal";

const Block = ({ testid, label, name, time, address, beginsAt, where }) => (
  <div data-testid={testid} className="relative p-10 sm:p-14">
    <div className="font-overline text-[10px] text-[color:var(--rose-300)]">{label}</div>
    <h3 className="mt-4 font-title text-4xl sm:text-5xl tracking-tighter leading-tight">{name}</h3>

    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6 text-white/85">
      <div className="flex items-start gap-3">
        <Clock size={16} className="mt-1 text-[color:var(--rose-300)]" />
        <div>
          <div className="font-overline text-[10px] text-white/50">{beginsAt}</div>
          <div className="font-serif text-2xl italic">{time}</div>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <MapPin size={16} className="mt-1 text-[color:var(--rose-300)]" />
        <div>
          <div className="font-overline text-[10px] text-white/50">{where}</div>
          <div className="font-sans text-base">{address}</div>
        </div>
      </div>
    </div>
  </div>
);

export const EventDetails = () => {
  const { t, lang } = useLang();
  const heading = t("details.heading");

  return (
    <section id="details" data-testid={DETAILS.section} className="relative bg-[#0A0A0A] text-[#FAFAFA] py-24 sm:py-32 overflow-hidden">
      <div className="absolute -top-32 right-1/3 w-[520px] h-[520px] rose-orb opacity-30 pointer-events-none" aria-hidden />
      <CallaLilyFlourish
        className="absolute -bottom-8 -right-6 w-[100px] sm:w-[140px] md:w-[180px]"
        opacity={0.8}
        variant="dark"
      />
      <CallaLilyFlourish
        className="absolute -top-6 -left-6 w-[100px] sm:w-[140px] md:w-[180px] rotate-180 scale-x-[-1]"
        opacity={0.75}
        variant="dark"
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div>
              <h2 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light">
                {heading[0]}
                <em className="italic">{heading[1]}</em>
                {heading[2]}
              </h2>
            </div>
            <div className="font-serif text-xl italic text-white/60 max-w-sm">
              {t("details.sub")}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-white/10">
            <div className="border-b md:border-b-0 md:border-r border-white/10">
              <Block
                testid={DETAILS.ceremony}
                label={t("details.act1Label")}
                name={WEDDING.venue.ceremony.name[lang]}
                time={WEDDING.venue.ceremony.time}
                address={WEDDING.venue.ceremony.address[lang]}
                beginsAt={t("details.beginsAt")}
                where={t("details.where")}
              />
            </div>
            <Block
              testid={DETAILS.reception}
              label={t("details.act2Label")}
              name={WEDDING.venue.reception.name[lang]}
              time={WEDDING.venue.reception.time}
              address={WEDDING.venue.reception.address[lang]}
              beginsAt={t("details.beginsAt")}
              where={t("details.where")}
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
